import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";

import './Venue.scss';

export const AdministratorVenueBlockedSecondaryTicketingWebsites = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const defaultSecondaryTicketingWebsite = {
        secondaryTicketingWebsiteId: "",
        name: "Select Website",
    };

    const [venueBlockedSecondaryTicketingWebsites, setVenueBlockedSecondaryTicketingWebsites] = useState([]);
    const [areVenueBlockedSecondaryTicketingWebsitesLoading, setAreVenueBlockedSecondaryTicketingWebsitesLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [selectedSecondaryTicketingWebsite, setSelectedSecondaryTicketingWebsite] = useState(defaultSecondaryTicketingWebsite);
    const [secondaryTicketingWebsites, setSecondaryTicketingWebsites] = useState(undefined);

    const getVenueBlockedSecondaryTicketingWebsites = () => {
        setAreVenueBlockedSecondaryTicketingWebsitesLoading(true);

        const dataItem = {
            venueKey: props.venueKey,
        };

        fetch(`/api/administrator/venueblockedsecondaryticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setVenueBlockedSecondaryTicketingWebsites(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setLoadingError(error.message);
            })
            .finally(() => {
                setAreVenueBlockedSecondaryTicketingWebsitesLoading(false);
            });
    }

    const deleteVenueBlockedSecondaryTicketingWebsite = (venueBlockedSecondaryTicketingWebsiteKey) => {
        setAreVenueBlockedSecondaryTicketingWebsitesLoading(true);

        const dataItem = {
            venueBlockedSecondaryTicketingWebsiteKey: venueBlockedSecondaryTicketingWebsiteKey
        }

        fetch("/api/administrator/venueblockedsecondaryticketingwebsite",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getVenueBlockedSecondaryTicketingWebsites();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueBlockedSecondaryTicketingWebsitesLoading(false);
            });
    }

    const blockSecondaryTicketingWebsite = () => {
        const secondaryTicketingWebsiteId = selectedSecondaryTicketingWebsite.secondaryTicketingWebsiteId;

        if (secondaryTicketingWebsiteId === "") {
            addNotificationWithTimeout(dispatch, "error", "You must select a website");
            return;
        }

        setAreVenueBlockedSecondaryTicketingWebsitesLoading(true);

        const dataItem = {
            venueKey: props.venueKey,
            secondaryTicketingWebsiteId: secondaryTicketingWebsiteId,
        }

        fetch("/api/administrator/venueblockedsecondaryticketingwebsite",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Blocked website for venue");

                getVenueBlockedSecondaryTicketingWebsites();

                setSelectedSecondaryTicketingWebsite(defaultSecondaryTicketingWebsite);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueBlockedSecondaryTicketingWebsitesLoading(false);
            });
    }

    const discard = (dataItem) => {
        const newVenueBlockedSecondaryTicketingWebsites = [...venueBlockedSecondaryTicketingWebsites];
        newVenueBlockedSecondaryTicketingWebsites.splice(0, 1);
        setVenueBlockedSecondaryTicketingWebsites(newVenueBlockedSecondaryTicketingWebsites);
    };

    const remove = (dataItem) => {
        deleteVenueBlockedSecondaryTicketingWebsite(dataItem.venueBlockedSecondaryTicketingWebsiteKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="venueBlockedSecondaryTicketingWebsiteKey"
        />
    );

    const getSecondaryTicketingWebsites = () => {
        fetch(`/api/administrator/secondaryticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allSecondaryTicketingWebsites = [defaultSecondaryTicketingWebsite].concat(result);
                setSecondaryTicketingWebsites(allSecondaryTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const secondaryTicketingWebsiteChanged = (event) => {
        setSelectedSecondaryTicketingWebsite(event.target.value);
    };

    useEffect(() => {
        if (props.venueKey) {
            getSecondaryTicketingWebsites();
            getVenueBlockedSecondaryTicketingWebsites();
        }
    }, []);

    return (
        <>
            {areVenueBlockedSecondaryTicketingWebsitesLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <>
                    <div className="row mb-3 mt-3">
                        <div className="col-md-6 col-lg-8">
                            <DropDownList
                                id="secondaryTicketingWebsite"
                                textField="name"
                                dataItemKey="secondaryTicketingWebsiteId"
                                data={secondaryTicketingWebsites}
                                value={selectedSecondaryTicketingWebsite}
                                onChange={secondaryTicketingWebsiteChanged}
                            />
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <Button
                                themeColor={"primary"}
                                type={"button"}
                                onClick={blockSecondaryTicketingWebsite}
                            >
                                Block Website
                            </Button>
                        </div>
                    </div>

                    <Grid
                        data={venueBlockedSecondaryTicketingWebsites}
                        editField={editField}
                        dataItemKey={"venueBlockedSecondaryTicketingWebsiteKey"}
                        resizable={true}
                        scrollable="none"
                    >
                        <Column field="secondaryTicketingWebsiteName" title={"Website"} />                        
                        <Column cell={MyCommandCell} width="150px" />
                    </Grid>
                </>
            ) : <></>)
            }
        </>
    );
}